import React, { useState } from "react";
import { ArrowUp, ArrowDown, ArrowRight, RotateCcw } from "lucide-react";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { Stave, Head, Single, fromMiddle, toClef, viewBox, ClefCtx } from "@/components/QuaverDraw";

const ROUND = 10;
const PITCHES = ["D4", "E4", "F4", "G4", "A4", "B4", "B4", "C5", "D5", "E5", "F5", "G5"];
const rightDirs = (p, clef) => (fromMiddle(p, clef) < 0 ? ["up"] : fromMiddle(p, clef) > 0 ? ["down"] : ["up", "down"]);
const why = (p, clef) => (fromMiddle(p, clef) < 0 ? `${p[0]} is below the middle line, so its stem points up (on the right).`
  : fromMiddle(p, clef) > 0 ? `${p[0]} is above the middle line, so its stem points down (on the left).`
    : `${p[0]} sits on the middle line, so the stem can point up or down. Both are correct.`);

// Level 2 of the quaver quiz: which way should the stem point?
export const StemDirectionRound = ({ testId, clef = "treble" }) => {
  const list = toClef(PITCHES, clef), makeQ = () => list[Math.floor(Math.random() * list.length)];
  const [p, setP] = useState(makeQ);
  const [n, setN] = useState(1);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);
  const ok = picked && rightDirs(p, clef).includes(picked);
  const pick = (d) => {
    if (picked) return;
    setPicked(d);
    const good = rightDirs(p, clef).includes(d);
    playResult(good);
    if (good) setScore((s) => s + 1);
  };
  const next = () => {
    if (n === ROUND) { setDone(true); if (score === ROUND) playFanfare(0.2, clef === "bass" ? "Stem direction quiz (bass clef)" : "Stem direction quiz"); return; }
    setP(makeQ()); setN(n + 1); setPicked(null);
  };
  const restart = () => { setP(makeQ()); setN(1); setScore(0); setPicked(null); setDone(false); };
  const btn = "inline-flex items-center gap-1.5 rounded-full border-2 px-5 py-2 text-sm font-bold transition-colors disabled:opacity-60";

  if (done) return (
    <div className="mt-4 text-center">
      <p className="text-base font-semibold" data-testid={`${testId}-result`}>{score === ROUND ? `Perfect! ${score} out of ${ROUND} — you earned a star.` : `You scored ${score} out of ${ROUND}.`}</p>
      <button type="button" onClick={restart} data-testid={`${testId}-restart`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><RotateCcw size={14} /> Play again</button>
    </div>
  );
  return (
    <ClefCtx.Provider value={clef}>
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">Which way should the stem point?</p>
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-progress`}>Question {n} of {ROUND} · Score {score}</p>
      </div>
      <div className={`mx-auto mt-3 max-w-xs rounded-xl border-2 bg-background/60 p-2 ${!picked ? "border-border" : ok ? "border-emerald-500" : "border-red-500"}`} data-testid={`${testId}-card`} data-pitch={p}>
        <svg viewBox={viewBox(0, 14, 100, 142, clef)} className={`mx-auto h-36 text-foreground ${clef === "bass" ? "w-40" : "w-32"}`} role="img" aria-label={`The note ${p[0]}`}>
          <Stave />
          {picked ? <Single x={50} p={p} quaver up={rightDirs(p, clef).length === 2 ? picked === "up" : rightDirs(p, clef)[0] === "up"} /> : <Head x={50} p={p} />}
        </svg>
      </div>
      {!picked ? (
        <div className="mt-3 flex justify-center gap-3">
          <button type="button" onClick={() => pick("up")} data-testid={`${testId}-up`} className={`${btn} border-primary text-primary hover:bg-primary/10`}><ArrowUp size={15} /> Stem up</button>
          <button type="button" onClick={() => pick("down")} data-testid={`${testId}-down`} className={`${btn} border-primary text-primary hover:bg-primary/10`}><ArrowDown size={15} /> Stem down</button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid={`${testId}-feedback`}>
          <p className={`text-sm font-semibold ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{ok ? "Correct! " : "Not quite. "}{why(p, clef)}</p>
          <button type="button" onClick={next} data-testid={`${testId}-next`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">
            {n === ROUND ? "See my score" : "Next"} <ArrowRight size={13} />
          </button>
        </div>
      )}
    </ClefCtx.Provider>
  );
};
